import { DSButton, DSFlex, DSText } from '@zoe/ds-web'
import { DSBadge } from '@zoe/ds-web'
import { DSSpacer } from '@zoe/ds-web'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React from 'react'
import styled from 'styled-components'

import { DSTextAndIcon } from '@/components/ds/TextAndIcon/TextAndIcon'
import { BREAKPOINTS } from '@/components/ds/breakpoints'

import { CurrencyCode } from '@/lib/checkout/types'
import { formatCurrency } from '@/lib/checkout/utils'
import { trackCtaClick } from '@/lib/gtm/gtmV2'

type D30PricingCardProps = {
  id: string
  onClick: () => void
  title: string
  price: number
  previousPrice?: number
  currency?: CurrencyCode
  featureList?: string[]
  isSelected: boolean
  ctaText?: string
  ctaLink?: string
  tagline?: string
  disabled?: boolean
  analyticsLinkText?: string
  label?: string
  banner?: string
  showRadioInTitle?: boolean
}

const StyledCard = styled.div<{ $active: boolean }>`
  background-color: ${({ $active }) => ($active ? 'var(--color-surface-cream-subtle)' : 'white')};
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  height: auto;
  overflow: hidden;
  padding: var(--grid-24) var(--grid-16);
  transition: background-color 0.3s ease-in-out;
`

const StyledPricingContainer = styled.div<{ $translate?: boolean }>`
  @media screen and (max-width: ${BREAKPOINTS.m}) {
    ${({ $translate }) => $translate && `transform: translateY(20%);`}
  }
`

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`

const OverlayAnchor = styled.a`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`

const StyledBanner = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid var(--colour-divider-brand);
  border-radius: var(--radius-12);
  padding: var(--grid-8);
  background-color: var(--color-surface-neutral);
`

export const D30PricingCard: React.FC<D30PricingCardProps> = ({
  id,
  onClick,
  title,
  price,
  label,
  previousPrice,
  currency = 'gbp',
  isSelected,
  ctaText,
  ctaLink,
  tagline,
  featureList,
  disabled = false,
  analyticsLinkText,
  banner,
  showRadioInTitle,
}) => {
  const router = useRouter()

  const handleCtaClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const linkText = analyticsLinkText ?? `${title.toLowerCase()}-Subscribe now`
    e.preventDefault()

    trackCtaClick('d30', linkText, {
      link_position: 'page_body',
      page_name: 'pdp',
    })
    router.push(e.currentTarget.href)
  }

  return (
    <StyledCard
      id={`pricing-card-${id}`}
      data-testid={`pricing-card-${id}`}
      $active={isSelected && !disabled}
      onClick={disabled ? undefined : onClick}
    >
      {label && (
        <>
          <DSBadge surfaceColor="yellow-bold" textColor="primary" testId={`d30-pdp-pricing-card-label-${id}`}>
            {label}
          </DSBadge>
          <DSSpacer size={12} direction="vertical" />
        </>
      )}
      <DSFlex direction="row" alignItems="flex-end" justifyContent="space-between" testId={`d30-pdp-title-${id}`}>
        <DSFlex
          alignItems="center"
          gap={12}
          breakConfig={{
            breakAt: { breakPoint: 'm', direction: 'column-reverse', gap: 12, alignItems: 'flex-start' },
            maxViewportDirection: 'row',
          }}
        >
          {disabled || !showRadioInTitle ? (
            <DSText variant="fluid-paragraph-300" weight="semiBold" color="primary">
              {title}
            </DSText>
          ) : (
            <DSTextAndIcon
              iconName={isSelected ? 'radio-selected' : 'radio-unselected'}
              iconPosition="left"
              iconSize={24}
              text={title}
              textVariant="fluid-paragraph-300"
              textWeight="semiBold"
              textColour="primary"
            />
          )}
        </DSFlex>
        <DSSpacer size={12} direction="horizontal" />
        <StyledPricingContainer $translate={!!label && !!previousPrice} data-testid={`d30-pdp-pricing-${id}`}>
          <DSFlex
            breakConfig={{
              breakAt: { breakPoint: 'm', direction: 'column', gap: 0 },
              maxViewportDirection: 'row-reverse',
            }}
          >
            <DSText as="span" variant="fluid-paragraph-300" weight="semiBold">
              {' '}
              {formatCurrency(currency, price, true)}
            </DSText>
            {previousPrice && (
              <>
                <DSSpacer size={8} direction="horizontal" />
                <DSText as="s" variant="fluid-paragraph-300" weight="light" color="placeholder">
                  {formatCurrency(currency, previousPrice, true)}
                </DSText>
              </>
            )}
          </DSFlex>
        </StyledPricingContainer>
      </DSFlex>
      <DSSpacer size={12} direction="vertical" />

      {banner && (
        <>
          <StyledBanner data-testid={`d30-pdp-pricing-card-banner-${id}`}>
            <Image
              src="https://www.datocms-assets.com/46938/1719581975-daily30-scoop-closeup.png?h=96&amp;q=75&amp;w96"
              alt="Tin and scoop"
              width={40}
              height={40}
              style={{ borderRadius: '8px', objectFit: 'cover', height: '40px', width: '40px' }}
            />
            <DSSpacer size={12} direction="horizontal" />
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              {banner}
            </DSText>
          </StyledBanner>
          <DSSpacer size={12} direction="vertical" />
        </>
      )}

      <div data-testid={`d30-pdp-feature-list-${id}`}>
        {featureList?.map((feature, i) => (
          <React.Fragment key={`feature-${i}`}>
            <DSTextAndIcon
              iconName="checkbox-tick"
              iconPosition="left"
              iconSize={16}
              text={feature}
              textColour="primary"
              textVariant="fluid-paragraph-200"
              textWeight="regular"
            />
            {i < featureList.length - 1 && <DSSpacer size={8} direction="vertical" />}
          </React.Fragment>
        ))}
      </div>
      {isSelected && !disabled && (
        <DSFlex direction="column" alignItems="center">
          <DSSpacer direction="vertical" size={16} />
          {ctaLink && ctaText && (
            <ButtonWrapper data-testid={`d30-pdp-cta-button-${id}`}>
              <DSButton size="large" variant="primary" width="full" colorScheme="default">
                {ctaText}
              </DSButton>
              <OverlayAnchor
                href={ctaLink}
                aria-label={ctaText}
                onClick={handleCtaClick}
                data-testid={`cta-button-${id}`}
              />
            </ButtonWrapper>
          )}
          <DSSpacer direction="vertical" size={16} />
          {tagline && (
            <DSText as="span" variant="fluid-paragraph-200" color="secondary" testId={`d30-pdp-tagline-${id}`}>
              {tagline}
            </DSText>
          )}
        </DSFlex>
      )}
    </StyledCard>
  )
}
